import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { SerializedStyles } from '@emotion/react';
import { Radio as AntdRadio } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { css } from 'twin.macro';
import { IRadioBlurHandler, IRadioChangeHandler, IRadioOption, IRadioProps, Radio } from './radio.component';

export interface IRadioGroupProps<TName = string> extends IFieldProps<string, TName> {
    className?: string;
    options?: IRadioOption[];
    onChange?: IRadioChangeHandler;
    onBlur?: IRadioBlurHandler;
    renderLabel?: (value: string) => JSX.Element;
    renderOption?: (props: IRadioProps, currentValue: string | undefined) => JSX.Element;
    horizontalDirection?: boolean;
}

export const RadioGroup = (props: IRadioGroupProps): JSX.Element => {
    const { options = [], value, renderOption, onChange, onBlur, renderLabel, defaultValue } = props;
    const [selected, setSelected] = useState<string | undefined>(value || defaultValue || options[0]?.value);

    useEffect(() => {
        setSelected(value);
    }, [value]);

    const handleChange = useCallback<IRadioChangeHandler>(
        ({ target }) => {
            setSelected(target.name);
            onChange && onChange({ target });
        },
        [onChange],
    );

    return (
        <Field {...props} labelCss={styles.label}>
            <AntdRadio.Group value={selected} tw="block" css={[styles.radioGroup(props.horizontalDirection)]}>
                {options.map((o) => {
                    if (renderOption) {
                        return (
                            <React.Fragment key={o.value}>
                                {renderOption(
                                    {
                                        option: o,
                                        onBlur,
                                        onChange,
                                        renderLabel,
                                        readOnly: props.readOnly,
                                    },
                                    value,
                                )}
                            </React.Fragment>
                        );
                    }
                    return (
                        <Radio
                            name={props.name}
                            readOnly={props.readOnly}
                            key={o.value}
                            option={o}
                            onBlur={onBlur}
                            renderLabel={renderLabel}
                            onChange={handleChange}
                        />
                    );
                })}
            </AntdRadio.Group>
        </Field>
    );
};

const styles = {
    label: css`
        display: block;
    `,
    radioGroup: (horizontalDirection: boolean | undefined): (SerializedStyles | boolean)[] => [
        !!horizontalDirection &&
            css`
                display: flex;
                justify-content: space-around;
            `,
    ],
};
